import { useState, useEffect, useCallback } from 'react';
import { Enrolled_User } from '../../services/oprations/Users';
import { Loader2, Fingerprint, CheckIcon, AlertCircle } from 'lucide-react';

export const useEnrollment = (devices, allUsers, dispatch, toast, refreshUsers, socket) => {
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [isEnrollPopupOpen, setIsEnrollPopupOpen] = useState(false);
  const [userToEnroll, setUserToEnroll] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollErrors, setEnrollErrors] = useState({});
  const [enrollmentStatus, setEnrollmentStatus] = useState('idle');
  const [enrollmentMessage, setEnrollmentMessage] = useState('');
  const [enrollmentComplete, setEnrollmentComplete] = useState(false);
  
  // Keep these for backward compatibility
  // eslint-disable-next-line
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  // eslint-disable-next-line
  const [stepStatus, setStepStatus] = useState({});
  // eslint-disable-next-line
  const [waitingForSocket, setWaitingForSocket] = useState(false);
  // eslint-disable-next-line
  const [stepError, setStepError] = useState(null);
  
  const [enrollment, setEnrollment] = useState({
    status: 'idle',
    message: 'Ready to start enrollment',
    step: 'initial'
  });

  const ENROLL_STEPS = [
    'CHECK_DUPLICATE', 'FIND_SLOT', 'FIRST_SCAN', 'REMOVE_FINGER',
    'SECOND_SCAN', 'CREATE_MODEL', 'STORE_MODEL'
  ];

  const devicesData = typeof devices === 'string' ? JSON.parse(devices) : (devices || []);

  const getAvailableDevicesForUser = (userDevices) => {
    const assignedDeviceIds = userDevices?.map(assignment => assignment.device?._id) || [];
    return devicesData.filter(device =>
      (device.last_status === 'ONLINE' || device.last_status === 'HEARTBEAT') &&
      !assignedDeviceIds.includes(device._id)
    );
  };

  const availableDevices = userToEnroll ? getAvailableDevicesForUser(userToEnroll.devices) : [];
  const selectedDeviceObj = devicesData.find(d => d._id === selectedDeviceId);

  // Socket handler for enrollment messages
  useEffect(() => {
    if (!socket) {
      console.log('[Socket] Socket not available');
      return;
    }

    const handleDeviceMessage = (data) => {
      console.log('[Socket] Device message received:', data);
      console.log('[Socket] Current enrollmentStatus:', enrollmentStatus);
      console.log('[Socket] Current selectedDeviceId:', selectedDeviceId);
      
      // Check if this message is for our device
      if (data.deviceId === selectedDeviceId) {
        console.log('[Socket] Device ID matches, updating state...');
        
        if (data.message === 'ENROLLMENT STARTED') {
          console.log('[Socket] Setting enrollment status to processing');
          setEnrollmentStatus('processing');
          setEnrollmentMessage('Enrollment started. Please follow device instructions...');
          setEnrollment({
            status: 'processing',
            message: 'Enrollment started. Follow device instructions...',
            step: 'started'
          });
          toast.info('Enrollment started on device');
        }
        else if (data.message === 'ENROLLMENT SUCCESS') {
          console.log('[Socket] Setting enrollment status to success');
          setEnrollmentStatus('success');
          setEnrollmentMessage('Enrollment completed successfully!');
          setEnrollmentComplete(true);
          setEnrollment({
            status: 'success',
            message: 'Enrollment completed successfully!',
            step: 'complete'
          });
          
          // Don't close immediately, let the user see the success message
          setTimeout(() => {
            console.log('[Socket] Closing popup after success');
            setIsEnrollPopupOpen(false);
            setIsEnrollModalOpen(false);
            setUserToEnroll(null);
            setSelectedDeviceId('');
            setIsEnrolling(false);
            setEnrollmentStatus('idle');
            setEnrollmentMessage('');
            refreshUsers();
            toast.success('Fingerprint enrolled successfully!');
          }, 3000);
        }
        else if (data.message === 'ENROLLMENT FAILED') {
          console.log('[Socket] Setting enrollment status to error');
          setEnrollmentStatus('error');
          setEnrollmentMessage(data.message || 'Enrollment failed');
          setEnrollment({
            status: 'error',
            message: data.message || 'Enrollment failed',
            step: 'error',
            error: data.message
          });
          toast.error(data.message || 'Enrollment failed');
          
          setTimeout(() => {
            setIsEnrollPopupOpen(false);
            setIsEnrolling(false);
            setEnrollmentStatus('idle');
            setEnrollmentMessage('');
          }, 3000);
        }
      } else {
        console.log('[Socket] Device ID mismatch. Expected:', selectedDeviceId, 'Got:', data.deviceId);
      }
    };

    socket.on('ENROLLMENT_PROCESS', handleDeviceMessage);
    socket.on('DEVICE_MESSAGE', handleDeviceMessage);
    
    return () => {
      socket.off('ENROLLMENT_PROCESS', handleDeviceMessage);
      socket.off('DEVICE_MESSAGE', handleDeviceMessage);
    };
  }, [socket, refreshUsers, toast, selectedDeviceId, enrollmentStatus]);

  const handleEnrollClick = (e, user) => {
    e.stopPropagation();
    setUserToEnroll(user);
    setSelectedDeviceId('');
    setEnrollErrors({});
    setEnrollmentStatus('idle');
    setEnrollmentMessage('');
    setEnrollmentComplete(false);
    setIsEnrollModalOpen(true);
  };

  const handleEnrollSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDeviceId) {
      setEnrollErrors({ device: 'Please select a device' });
      return;
    }

    console.log('[Enrollment] Starting enrollment submission');
    setIsEnrolling(true);
    setIsEnrollModalOpen(false);
    setIsEnrollPopupOpen(true);
    
    // Set initial processing state
    setEnrollmentStatus('processing');
    setEnrollmentMessage('Starting enrollment...');
    setEnrollment({
      status: 'processing',
      message: 'Starting enrollment...',
      step: 'starting'
    });

    try {
      const res = await dispatch(Enrolled_User(
        userToEnroll._id,
        selectedDeviceId
      ));
      
      console.log('[Enrollment] API Response:', res);
      
      if (!res?.data?.success) {
        console.log('[Enrollment] API returned error');
        setEnrollmentStatus('error');
        setEnrollmentMessage(res?.data?.message || 'Failed to start enrollment');
        setEnrollment({
          status: 'error',
          message: res?.data?.message || 'Failed to start enrollment',
          step: 'error',
          error: res?.data?.message
        });
        toast.error(res?.data?.message || 'Failed to start enrollment');
        
        setTimeout(() => {
          setIsEnrollPopupOpen(false);
          setIsEnrolling(false);
          setEnrollmentStatus('idle');
          setEnrollmentMessage('');
        }, 3000);
      } else {
        console.log('[Enrollment] API success, waiting for device response...');
        // Don't change status here - wait for socket message
        setEnrollmentMessage('Waiting for device response...');
      }
    } catch (error) {
      console.error('[Enrollment] Error:', error);
      setEnrollmentStatus('error');
      setEnrollmentMessage(error.message || 'Failed to start enrollment');
      setEnrollment({
        status: 'error',
        message: error.message || 'Failed to start enrollment',
        step: 'error',
        error: error.message
      });
      toast.error(error.message || 'Failed to start enrollment');
      
      setTimeout(() => {
        setIsEnrollPopupOpen(false);
        setIsEnrolling(false);
        setEnrollmentStatus('idle');
        setEnrollmentMessage('');
      }, 3000);
    }
  };

  const closeEnrollModal = () => {
    if (!isEnrolling) {
      setIsEnrollModalOpen(false);
      setUserToEnroll(null);
      setSelectedDeviceId('');
      setEnrollErrors({});
      setEnrollmentStatus('idle');
      setEnrollmentMessage('');
    }
  };

  const handleCloseEnrollPopup = useCallback(() => {
    console.log('[Enrollment] Closing popup');
    setIsEnrollPopupOpen(false);
    setEnrollment({ status: 'idle', message: 'Enrollment cancelled', step: 'initial' });
    setIsEnrolling(false);
    setEnrollmentStatus('idle');
    setEnrollmentMessage('');
    setEnrollmentComplete(false);
    toast.warning('Enrollment cancelled');
  }, [toast]);

  // Dummy functions for step-based enrollment
  const handleStartStep = async () => {};
  const handleRetryStep = async () => {};
  const handleGoToPreviousStep = () => {};
  const handleResetCurrentStep = () => {};

  const getEnrollmentStatusIcon = () => {
    const size = 'w-16 h-16';
    const icons = {
      idle: <Fingerprint className={`${size} text-blue-400`} />,
      processing: <Loader2 className={`${size} text-blue-400 animate-spin`} />,
      success: <CheckIcon className={`${size} text-green-400`} />,
      error: <AlertCircle className={`${size} text-red-400`} />,
    };
    return icons[enrollmentStatus] || icons.processing;
  };

  const getEnrollmentStatusColor = () => {
    const colors = {
      idle: 'text-blue-400',
      processing: 'text-blue-400',
      success: 'text-green-400',
      error: 'text-red-400',
    };
    return colors[enrollmentStatus] || 'text-blue-400';
  };

  return {
    isEnrollModalOpen,
    setIsEnrollModalOpen,
    isEnrollPopupOpen,
    setIsEnrollPopupOpen,
    enrollment,
    setEnrollment,
    enrollmentComplete,
    userToEnroll,
    setUserToEnroll,
    selectedDeviceId,
    setSelectedDeviceId,
    availableDevices,
    enrollErrors,
    isEnrolling,
    setIsEnrolling,
    handleEnrollClick,
    handleEnrollSubmit,
    closeEnrollModal,
    handleCloseEnrollPopup,
    getEnrollmentStatusIcon,
    getEnrollmentStatusColor,
    selectedDeviceObj,
    currentStepIndex,
    stepStatus,
    stepError,
    waitingForSocket,
    handleStartStep,
    handleRetryStep,
    handleGoToPreviousStep,
    handleResetCurrentStep,
    ENROLL_STEPS,
    enrollmentStatus,
    enrollmentMessage
  };
};

// import { useState, useEffect, useRef, useCallback } from 'react';
// import { Enrolled_User } from '../../services/oprations/Users';
// import { Loader2, Fingerprint, X, CheckIcon, AlertCircle } from 'lucide-react';

// export const useEnrollment = (devices, allUsers, dispatch, toast, refreshUsers, socket) => {
//   const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
//   const [isEnrollPopupOpen, setIsEnrollPopupOpen] = useState(false);
//   const [userToEnroll, setUserToEnroll] = useState(null);
//   const [selectedDeviceId, setSelectedDeviceId] = useState('');
//   const [isEnrolling, setIsEnrolling] = useState(false);
//   const [enrollErrors, setEnrollErrors] = useState({});
//   const [currentStepIndex, setCurrentStepIndex] = useState(0);
//   const [stepStatus, setStepStatus] = useState({});
//   const [waitingForSocket, setWaitingForSocket] = useState(false);
//   const [socketTimeout, setSocketTimeout] = useState(null);
//   const [retryCount, setRetryCount] = useState(0);
//   const [stepError, setStepError] = useState(null);
  
//   const [enrollment, setEnrollment] = useState({
//     status: 'idle',
//     message: 'Ready to start enrollment',
//     step: 'initial'
//   });

//   const enrollCtxRef = useRef({ 
//     userToEnroll: null, 
//     selectedDeviceId: '', 
//     isEnrolling: false,
//     currentStepIndex: 0,
//     timeoutId: null
//   });

//   const devicesData = typeof devices === 'string' ? JSON.parse(devices) : (devices || []);

//   const ENROLL_STEPS = [
//     'CHECK_DUPLICATE', 'FIND_SLOT', 'FIRST_SCAN', 'REMOVE_FINGER',
//     'SECOND_SCAN', 'CREATE_MODEL', 'STORE_MODEL'
//   ];

//   const STEP_TIMEOUT = 10000; // 10 seconds timeout for each step

//   const getAvailableDevicesForUser = (userDevices) => {
//     const assignedDeviceIds = userDevices?.map(assignment => assignment.device?._id) || [];
//     return devicesData.filter(device =>
//       (device.last_status === 'ONLINE' || device.last_status === 'HEARTBEAT') &&
//       !assignedDeviceIds.includes(device._id)
//     );
//   };

//   const availableDevices = userToEnroll ? getAvailableDevicesForUser(userToEnroll.devices) : [];
//   const selectedDeviceObj = devicesData.find(d => d._id === selectedDeviceId);

//   // Clear timeout function
//   const clearStepTimeout = () => {
//     if (enrollCtxRef.current.timeoutId) {
//       clearTimeout(enrollCtxRef.current.timeoutId);
//       enrollCtxRef.current.timeoutId = null;
//     }
//     setSocketTimeout(null);
//   };

//   // Set timeout for current step
//   const setStepTimeout = (step, stepIdx) => {
//     clearStepTimeout();
    
//     const timeoutId = setTimeout(() => {
//       console.log(`[Enrollment] Timeout for step ${step} after ${STEP_TIMEOUT}ms`);
//       setWaitingForSocket(false);
//       setStepError(`Step "${step}" timed out. No response from device.`);
//       setStepStatus(prev => ({
//         ...prev,
//         [step]: 'timeout'
//       }));
//       setEnrollment({
//         status: 'timeout',
//         message: `Step ${stepIdx + 1} timed out. No response from device.`,
//         step: step,
//         error: 'Timeout waiting for device response'
//       });
//       toast.error(`Step "${step}" timed out. Please retry or check device connection.`);
//     }, STEP_TIMEOUT);
    
//     enrollCtxRef.current.timeoutId = timeoutId;
//     setSocketTimeout(timeoutId);
//   };

//   // Keep ref in sync
//   useEffect(() => {
//     enrollCtxRef.current = { 
//       userToEnroll, 
//       selectedDeviceId, 
//       isEnrolling,
//       currentStepIndex,
//       timeoutId: enrollCtxRef.current.timeoutId
//     };
//   }, [userToEnroll, selectedDeviceId, isEnrolling, currentStepIndex]);

//   // Reset state when modal opens
//   useEffect(() => {
//     if (isEnrollPopupOpen) {
//       setCurrentStepIndex(0);
//       setStepStatus({});
//       setWaitingForSocket(false);
//       setStepError(null);
//       setRetryCount(0);
//       clearStepTimeout();
//       setEnrollment({
//         status: 'idle',
//         message: 'Ready to start enrollment',
//         step: 'initial'
//       });
//     }
//     return () => {
//       clearStepTimeout();
//     };
//   }, [isEnrollPopupOpen]);

//   // Socket handler for enrollment progress
//   useEffect(() => {
//     if (!socket) return;

//     const handleEnrollProgress = (data) => {
//       console.log('[Socket] Enrollment progress:', data);
      
//       const { Result, Step, deviceId, message, FingerId } = data;
//       const { selectedDeviceId: ctxDeviceId, currentStepIndex: ctxStepIndex } = enrollCtxRef.current;

//       // Verify this is for our device and current step
//       if (deviceId !== ctxDeviceId) {
//         console.log('[Socket] Ignoring - device mismatch');
//         return;
//       }

//       // Check if this is the expected step
//       const expectedStep = ENROLL_STEPS[ctxStepIndex];
//       if (Step !== expectedStep) {
//         console.log(`[Socket] Step mismatch - Expected: ${expectedStep}, Got: ${Step}`);
//         return;
//       }

//       // Clear timeout since we received response
//       clearStepTimeout();

//       if (Result === 'SUCCESS') {
//         // Step completed successfully
//         setStepStatus(prev => ({
//           ...prev,
//           [Step]: 'success'
//         }));
//         setWaitingForSocket(false);
//         setStepError(null);
//         setRetryCount(0);
        
//         setEnrollment({
//           status: 'success',
//           message: message || `Step ${ctxStepIndex + 1} completed successfully!`,
//           step: Step
//         });

//         // Check if this was the last step
//         if (ctxStepIndex === ENROLL_STEPS.length - 1) {
//           // All steps completed
//           setTimeout(() => {
//             setIsEnrollPopupOpen(false);
//             setIsEnrollModalOpen(false);
//             setUserToEnroll(null);
//             setSelectedDeviceId('');
//             setIsEnrolling(false);
//             setCurrentStepIndex(0);
//             setStepStatus({});
//             clearStepTimeout();
//             refreshUsers();
//             toast.success('Fingerprint enrolled successfully!');
//           }, 2000);
//         } else {
//           // Move to next step after delay
//           setTimeout(() => {
//             setCurrentStepIndex(prev => prev + 1);
//             setEnrollment({
//               status: 'idle',
//               message: `Ready for step ${ctxStepIndex + 2}`,
//               step: 'idle'
//             });
//           }, 1500);
//         }
//       } else if (Result === 'FAILED' || Result === 'DUPLICATE') {
//         // Step failed
//         setStepStatus(prev => ({
//           ...prev,
//           [Step]: 'failed'
//         }));
//         setWaitingForSocket(false);
//         setStepError(message || 'Step failed');
//         setEnrollment({
//           status: 'error',
//           message: message || `Step ${ctxStepIndex + 1} failed`,
//           step: Step,
//           error: message
//         });
//         toast.error(message || `Step ${ctxStepIndex + 1} failed`);
//       }
//     };

//     socket.on('ENROLLMENT_STEP', handleEnrollProgress);
//     return () => {
//       socket.off('ENROLLMENT_STEP', handleEnrollProgress);
//       clearStepTimeout();
//     };
//   }, [socket, refreshUsers, toast, setIsEnrollPopupOpen, setIsEnrollModalOpen, setUserToEnroll, setSelectedDeviceId, setIsEnrolling]);

//   const handleEnrollClick = (e, user) => {
//     e.stopPropagation();
//     setUserToEnroll(user);
//     setSelectedDeviceId('');
//     setEnrollErrors({});
//     setCurrentStepIndex(0);
//     setStepStatus({});
//     setStepError(null);
//     setRetryCount(0);
//     clearStepTimeout();
//     setIsEnrollModalOpen(true);
//   };

//   const handleStartStep = async (stepKey, stepIndex) => {
//     if (!userToEnroll || !selectedDeviceId) return;
    
//     setWaitingForSocket(true);
//     setStepError(null);
//     setStepStatus(prev => ({
//       ...prev,
//       [stepKey]: 'processing'
//     }));
    
//     setEnrollment({
//       status: 'processing',
//       message: `Processing step ${stepIndex + 1}: ${stepKey}`,
//       step: stepKey
//     });

//     // Set timeout for this step
//     setStepTimeout(stepKey, stepIndex);

//     try {
//       const res = await dispatch(Enrolled_User(
//         userToEnroll._id,
//         selectedDeviceId,
//         stepKey,
//         -1
//       ));
      
//       if (!res?.data?.success) {
//         clearStepTimeout();
//         setStepStatus(prev => ({
//           ...prev,
//           [stepKey]: 'failed'
//         }));
//         setWaitingForSocket(false);
//         setStepError(res?.data?.message || 'Failed to start step');
//         setEnrollment({
//           status: 'error',
//           message: res?.data?.message || 'Failed to start step',
//           step: stepKey,
//           error: res?.data?.message
//         });
//         toast.error(res?.data?.message || 'Failed to start step');
//       }
//     } catch (error) {
//       clearStepTimeout();
//       setStepStatus(prev => ({
//         ...prev,
//         [stepKey]: 'failed'
//       }));
//       setWaitingForSocket(false);
//       setStepError(error.message);
//       setEnrollment({
//         status: 'error',
//         message: error.message,
//         step: stepKey,
//         error: error.message
//       });
//       toast.error(error.message);
//     }
//   };

//   const handleRetryStep = async (stepKey, stepIndex) => {
//     setRetryCount(prev => prev + 1);
//     setStepError(null);
//     setStepStatus(prev => ({
//       ...prev,
//       [stepKey]: 'pending'
//     }));
//     await handleStartStep(stepKey, stepIndex);
//   };

//   const handleGoToPreviousStep = () => {
//     if (currentStepIndex > 0 && !waitingForSocket) {
//       clearStepTimeout();
//       const previousStep = ENROLL_STEPS[currentStepIndex - 1];
//       setCurrentStepIndex(prev => prev - 1);
//       setStepStatus(prev => ({
//         ...prev,
//         [previousStep]: 'pending'
//       }));
//       setStepError(null);
//       setEnrollment({
//         status: 'idle',
//         message: `Returned to step ${currentStepIndex}`,
//         step: 'idle'
//       });
//       toast.info(`Returned to previous step`);
//     }
//   };

//   const handleResetCurrentStep = () => {
//     if (!waitingForSocket) {
//       const currentStep = ENROLL_STEPS[currentStepIndex];
//       clearStepTimeout();
//       setStepStatus(prev => ({
//         ...prev,
//         [currentStep]: 'pending'
//       }));
//       setStepError(null);
//       setWaitingForSocket(false);
//       setEnrollment({
//         status: 'idle',
//         message: `Reset step ${currentStepIndex + 1}. Ready to retry.`,
//         step: 'idle'
//       });
//       toast.info(`Step reset. You can now retry.`);
//     }
//   };

//   const handleEnrollSubmit = async (e) => {
//     e.preventDefault();

//     if (!selectedDeviceId) {
//       setEnrollErrors({ device: 'Please select a device' });
//       return;
//     }

//     setIsEnrolling(true);
//     setIsEnrollModalOpen(false);
//     setIsEnrollPopupOpen(true);
//     setCurrentStepIndex(0);
//     setStepStatus({});
//     setStepError(null);
//     setRetryCount(0);
    
//     // Start the first step
//     await handleStartStep(ENROLL_STEPS[0], 0);
//   };

//   const closeEnrollModal = () => {
//     if (!isEnrolling) {
//       clearStepTimeout();
//       setIsEnrollModalOpen(false);
//       setUserToEnroll(null);
//       setSelectedDeviceId('');
//       setEnrollErrors({});
//       setCurrentStepIndex(0);
//       setStepStatus({});
//       setStepError(null);
//       setRetryCount(0);
//     }
//   };

//   // const handleCloseEnrollPopup = useCallback(() => {
//   //   if (!waitingForSocket) {
//   //     clearStepTimeout();
//   //     setIsEnrollPopupOpen(false);
//   //     setEnrollment({ status: 'idle', message: 'Enrollment cancelled', step: 'initial' });
//   //     setIsEnrolling(false);
//   //     setCurrentStepIndex(0);
//   //     setStepStatus({});
//   //     setWaitingForSocket(false);
//   //     setStepError(null);
//   //     setRetryCount(0);
//   //     toast.warning('Enrollment cancelled');
//   //   }
//   // }, [toast, waitingForSocket]);
//   const handleCloseEnrollPopup = useCallback(() => {
//     clearStepTimeout();
//     setIsEnrollPopupOpen(false);
//     setEnrollment({ status: 'idle', message: 'Enrollment cancelled', step: 'initial' });
//     setIsEnrolling(false);
//     setCurrentStepIndex(0);
//     setStepStatus({});
//     setWaitingForSocket(false);
//     setStepError(null);
//     setRetryCount(0);
//     toast.warning('Enrollment cancelled');
//   }, [toast]);

//   const getEnrollmentStatusIcon = () => {
//     const size = 'w-16 h-16';
//     const icons = {
//       idle: <Fingerprint className={`${size} text-blue-400`} />,
//       processing: <Loader2 className={`${size} text-blue-400 animate-spin`} />,
//       success: <CheckIcon className={`${size} text-green-400`} />,
//       error: <AlertCircle className={`${size} text-red-400`} />,
//       timeout: <AlertCircle className={`${size} text-orange-400`} />,
//     };
//     return icons[enrollment.status] || icons.processing;
//   };

//   const getEnrollmentStatusColor = () => {
//     const colors = {
//       idle: 'text-blue-400',
//       processing: 'text-blue-400',
//       success: 'text-green-400',
//       error: 'text-red-400',
//       timeout: 'text-orange-400',
//     };
//     return colors[enrollment.status] || 'text-blue-400';
//   };

//   return {
//     isEnrollModalOpen,
//     setIsEnrollModalOpen,
//     isEnrollPopupOpen,
//     setIsEnrollPopupOpen,
//     enrollment,
//     setEnrollment,
//     userToEnroll,
//     setUserToEnroll,
//     selectedDeviceId,
//     setSelectedDeviceId,
//     availableDevices,
//     enrollErrors,
//     isEnrolling,
//     setIsEnrolling,
//     handleEnrollClick,
//     handleEnrollSubmit,
//     closeEnrollModal,
//     handleCloseEnrollPopup,
//     getEnrollmentStatusIcon,
//     getEnrollmentStatusColor,
//     selectedDeviceObj,
//     // New props for manual step control with retry
//     currentStepIndex,
//     stepStatus,
//     waitingForSocket,
//     socketTimeout,
//     stepError,
//     retryCount,
//     handleStartStep,
//     handleRetryStep,
//     handleGoToPreviousStep,
//     handleResetCurrentStep,
//     ENROLL_STEPS
//   };
// };
